import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { USER_ROLES } from '../config/security'
import {
  listarColunasVisiveis,
  listarLeads,
  criarLead,
  atualizarLead,
  inativarLead,
  moverLead,
  efetivarContrato,
  criarColuna,
  alternarFixacaoColuna,
  renomearColuna,
  inativarColuna,
  reativarColuna,
  listarColunasInativas,
  reordenarColunas,
  listarHistoricoLead,
  listarHistoricoMovimentacoes,
  listarUsuariosEmpresa,
  listarCompartilhamentos,
  salvarCompartilhamentos,
  podeGerenciarCompartilhamento,
  podeGerenciarColuna,
  listarCompartilhamentosCard,
  salvarCompartilhamentosCard,
  podeGerenciarCompartilhamentoCard
} from '../services/gestaoLeadsApi'
import GestaoLeadsRelatorio from './GestaoLeadsRelatorio'
import './GestaoLeads.css'

/** Roles com privilégio de gestão de estrutura (quadros): fixar, reordenar, ver todos. */
const ROLES_PRIVILEGIADOS = [USER_ROLES.ADMINISTRADOR, USER_ROLES.ADMINISTRATIVO]

const COOKIE_HIST_INI = 'gestao_leads_hist_data_inicio'
const COOKIE_HIST_FIM = 'gestao_leads_hist_data_fim'

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

function setCookie(name, value, days = 180) {
  const maxAge = days * 24 * 60 * 60
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`
}

function getMesAtualRange() {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const lastDay = new Date(y, now.getMonth() + 1, 0).getDate()
  return {
    inicio: `${y}-${m}-01`,
    fim: `${y}-${m}-${String(lastDay).padStart(2, '0')}`
  }
}

function getDatasHistoricoIniciais() {
  const mes = getMesAtualRange()
  const ini = getCookie(COOKIE_HIST_INI)
  const fim = getCookie(COOKIE_HIST_FIM)
  return {
    dataInicio: ini || mes.inicio,
    dataFim: fim || mes.fim
  }
}

const GestaoLeads = ({ onBack, userRole, userEmpresa, userId, userName }) => {
  const isPrivilegiado = ROLES_PRIVILEGIADOS.includes(userRole)
  const empresaId = userEmpresa?.id || userEmpresa

  const [colunas, setColunas] = useState([])
  const [leads, setLeads] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)
  /** Admin/Administrativo: por padrão vê todos; pode alterar para só os próprios. */
  const [verTodosCards, setVerTodosCards] = useState(true)
  /** Filtro multi-usuário dos cards no board (privilegiado). Vazio = todos. */
  const [filtroUsuariosIds, setFiltroUsuariosIds] = useState([])
  const [showFiltroUsuarios, setShowFiltroUsuarios] = useState(false)
  const [usuariosFiltroLista, setUsuariosFiltroLista] = useState([])
  const filtroUsuariosRef = useRef(null)

  const [modalLead, setModalLead] = useState(null) // null | 'create' | lead
  const [modalView, setModalView] = useState(null)
  const [modalColumn, setModalColumn] = useState(false)
  const [modalRenameColumn, setModalRenameColumn] = useState(null)
  const [modalInactiveColumns, setModalInactiveColumns] = useState(false)
  const [colunasInativas, setColunasInativas] = useState([])
  const [modalContract, setModalContract] = useState(null)
  const [modalHistorico, setModalHistorico] = useState(null)
  const [historicoItems, setHistoricoItems] = useState([])
  const [modalRelatorio, setModalRelatorio] = useState(false)
  const [modalHistoricoGeral, setModalHistoricoGeral] = useState(false)
  const [historicoGeralItems, setHistoricoGeralItems] = useState([])
  const [historicoGeralLoading, setHistoricoGeralLoading] = useState(false)
  const [filtroHistorico, setFiltroHistorico] = useState(() => ({
    ...getDatasHistoricoIniciais(),
    usuarioId: ''
  }))
  const [modalShare, setModalShare] = useState(null)
  const [modalShareLead, setModalShareLead] = useState(null)
  const [usuariosEmpresa, setUsuariosEmpresa] = useState([])
  const [shareSelectedIds, setShareSelectedIds] = useState([])
  const [shareSearch, setShareSearch] = useState('')

  const [formLead, setFormLead] = useState({ nome: '', telefone: '', observacoes: '', colunaId: '' })
  const [formColumn, setFormColumn] = useState({ titulo: '' })
  const [formRenameColumn, setFormRenameColumn] = useState({ titulo: '' })
  const [formContract, setFormContract] = useState({
    razaoSocial: '',
    documento: '',
    emailFinanceiro: '',
    valorContrato: ''
  })

  const draggedType = useRef(null)
  const draggedCardId = useRef(null)
  const draggedColumnId = useRef(null)

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type })
    setTimeout(() => setMessage(null), 3500)
  }

  const colunasMap = useMemo(() => {
    const map = {}
    colunas.forEach((c) => { map[c.id] = c })
    return map
  }, [colunas])

  const leadsPorColuna = useMemo(() => {
    const map = {}
    colunas.forEach((c) => { map[c.id] = [] })
    leads.forEach((l) => {
      if (map[l.coluna_id]) map[l.coluna_id].push(l)
      else if (colunas[0]) {
        // lead órfão → primeira coluna (não deve ocorrer)
      }
    })
    Object.keys(map).forEach((id) => {
      map[id].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
    })
    return map
  }, [colunas, leads])

  const carregar = useCallback(async () => {
    if (!empresaId || !userId) {
      setLoading(false)
      showMsg('Empresa/usuário não identificados para carregar o Kanban.', 'error')
      return
    }
    try {
      setLoading(true)
      const cols = await listarColunasVisiveis({
        empresaId,
        usuarioId: userId,
        isPrivilegiado
      })
      const cards = await listarLeads({
        empresaId,
        colunaIds: cols.map((c) => c.id),
        usuarioId: userId,
        isPrivilegiado,
        verTodos: isPrivilegiado && verTodosCards,
        filtroUsuarioIds: isPrivilegiado && verTodosCards ? filtroUsuariosIds : null
      })
      setColunas(cols)
      setLeads(cards)
    } catch (err) {
      console.error(err)
      showMsg('Erro ao carregar Gestão Leads. Verifique se as tabelas (142–144) foram criadas no banco.', 'error')
    } finally {
      setLoading(false)
    }
  }, [empresaId, userId, isPrivilegiado, verTodosCards, filtroUsuariosIds])

  useEffect(() => {
    carregar()
  }, [carregar])

  useEffect(() => {
    if (!isPrivilegiado || !empresaId) return undefined
    let cancelled = false
    listarUsuariosEmpresa(empresaId)
      .then((users) => {
        if (!cancelled) setUsuariosFiltroLista(users || [])
      })
      .catch((err) => console.error(err))
    return () => { cancelled = true }
  }, [isPrivilegiado, empresaId])

  useEffect(() => {
    if (!showFiltroUsuarios) return undefined
    const onDocClick = (e) => {
      if (filtroUsuariosRef.current && !filtroUsuariosRef.current.contains(e.target)) {
        setShowFiltroUsuarios(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [showFiltroUsuarios])

  const openCreateLead = (colunaPreSelecionada = null) => {
    const colunaInicial = colunaPreSelecionada
      || colunas.find((c) => c.slug === 'novo-lead')
      || colunas[0]
    setFormLead({
      nome: '',
      telefone: '',
      observacoes: '',
      colunaId: colunaInicial?.id || ''
    })
    setModalLead('create')
  }

  const openEditLead = (lead) => {
    setFormLead({
      nome: lead.nome || '',
      telefone: lead.telefone || '',
      observacoes: lead.observacoes || '',
      colunaId: lead.coluna_id || ''
    })
    setModalLead(lead)
  }

  const openShareModal = async (coluna) => {
    if (!podeGerenciarCompartilhamento(coluna, userId, isPrivilegiado)) {
      showMsg('Somente o criador do quadro (ou privilegiado) pode compartilhar.', 'error')
      return
    }
    try {
      setSaving(true)
      setModalShare(coluna)
      setShareSearch('')
      const [users, comps] = await Promise.all([
        listarUsuariosEmpresa(empresaId),
        listarCompartilhamentos(coluna.id)
      ])
      setUsuariosEmpresa(users.filter((u) => u.id !== userId))
      setShareSelectedIds(comps.map((c) => c.usuario_id))
    } catch (err) {
      console.error(err)
      showMsg('Erro ao abrir compartilhamento.', 'error')
      setModalShare(null)
    } finally {
      setSaving(false)
    }
  }

  const handleSaveShare = async (e) => {
    e.preventDefault()
    if (!modalShare) return
    try {
      setSaving(true)
      await salvarCompartilhamentos({
        coluna: modalShare,
        usuarioIds: shareSelectedIds,
        compartilhadoPor: userId,
        nomeUsuario: userName
      })
      setModalShare(null)
      showMsg('Compartilhamento atualizado! Os usuários selecionados já podem ver o quadro.')
    } catch (err) {
      console.error(err)
      showMsg('Erro ao salvar compartilhamento.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const openShareLeadModal = async (lead) => {
    if (!podeGerenciarCompartilhamentoCard(lead, userId, isPrivilegiado)) {
      showMsg('Somente o dono do card (ou privilegiado) pode compartilhar.', 'error')
      return
    }
    try {
      setSaving(true)
      setModalShareLead(lead)
      setShareSearch('')
      const [users, comps] = await Promise.all([
        listarUsuariosEmpresa(empresaId),
        listarCompartilhamentosCard(lead.id)
      ])
      const donoId = lead.created_by || lead.responsavel_id
      setUsuariosEmpresa(users.filter((u) => u.id !== donoId))
      setShareSelectedIds(comps.map((c) => c.usuario_id))
    } catch (err) {
      console.error(err)
      showMsg('Erro ao abrir compartilhamento do card.', 'error')
      setModalShareLead(null)
    } finally {
      setSaving(false)
    }
  }

  const handleSaveShareLead = async (e) => {
    e.preventDefault()
    if (!modalShareLead) return
    try {
      setSaving(true)
      await salvarCompartilhamentosCard({
        lead: modalShareLead,
        usuarioIds: shareSelectedIds,
        compartilhadoPor: userId,
        nomeUsuario: userName
      })
      setModalShareLead(null)
      showMsg('Card compartilhado! Os usuários selecionados já podem visualizá-lo.')
    } catch (err) {
      console.error(err)
      showMsg('Erro ao salvar compartilhamento do card.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const toggleShareUser = (usuarioId) => {
    setShareSelectedIds((prev) =>
      prev.includes(usuarioId)
        ? prev.filter((id) => id !== usuarioId)
        : [...prev, usuarioId]
    )
  }

  const handleSaveLead = async (e) => {
    e.preventDefault()
    if (!formLead.nome.trim() || !formLead.telefone.trim()) return

    try {
      setSaving(true)
      if (modalLead === 'create') {
        const colunaId = formLead.colunaId || colunas[0]?.id
        if (!colunaId) throw new Error('Nenhuma coluna disponível')
        if (!colunas.some((c) => c.id === colunaId)) {
          showMsg('Você não tem acesso a este quadro.', 'error')
          return
        }
        const novo = await criarLead({
          empresaId,
          colunaId,
          nome: formLead.nome.trim(),
          telefone: formLead.telefone.trim(),
          observacoes: formLead.observacoes.trim(),
          usuarioId: userId,
          nomeUsuario: userName
        })
        setLeads((prev) => [...prev, novo])
        showMsg('Lead criado com sucesso!')
      } else if (modalLead?.id) {
        const atualizado = await atualizarLead({
          lead: modalLead,
          nome: formLead.nome.trim(),
          telefone: formLead.telefone.trim(),
          observacoes: formLead.observacoes.trim(),
          usuarioId: userId,
          nomeUsuario: userName
        })
        setLeads((prev) => prev.map((l) => (l.id === atualizado.id ? atualizado : l)))
        showMsg('Lead atualizado!')
      }
      setModalLead(null)
    } catch (err) {
      console.error(err)
      showMsg('Erro ao salvar lead.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleInativarLead = async (lead) => {
    if (!window.confirm(`Inativar o lead "${lead.nome}"? Ele sairá da visualização (somente ativos).`)) return
    try {
      setSaving(true)
      await inativarLead({ lead, usuarioId: userId, nomeUsuario: userName })
      setLeads((prev) => prev.filter((l) => l.id !== lead.id))
      setModalView(null)
      showMsg('Lead inativado.')
    } catch (err) {
      console.error(err)
      showMsg('Erro ao inativar lead.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const openHistorico = async (lead) => {
    try {
      setModalHistorico(lead)
      const items = await listarHistoricoLead(lead.id)
      setHistoricoItems(items)
    } catch (err) {
      console.error(err)
      showMsg('Erro ao carregar histórico.', 'error')
    }
  }

  const carregarHistoricoGeral = useCallback(async (filtros) => {
    if (!empresaId || !filtros?.dataInicio || !filtros?.dataFim) return
    try {
      setHistoricoGeralLoading(true)
      const items = await listarHistoricoMovimentacoes({
        empresaId,
        dataInicio: filtros.dataInicio,
        dataFim: filtros.dataFim,
        usuarioId: filtros.usuarioId || null
      })
      setHistoricoGeralItems(items)
    } catch (err) {
      console.error(err)
      showMsg('Erro ao carregar histórico de movimentações.', 'error')
    } finally {
      setHistoricoGeralLoading(false)
    }
  }, [empresaId])

  const openHistoricoGeral = async () => {
    const datas = getDatasHistoricoIniciais()
    const filtros = {
      dataInicio: datas.dataInicio,
      dataFim: datas.dataFim,
      usuarioId: ''
    }
    setFiltroHistorico(filtros)
    setModalHistoricoGeral(true)
    await carregarHistoricoGeral(filtros)
  }

  const handleChangeFiltroHistorico = (campo, valor) => {
    setFiltroHistorico((prev) => {
      const next = { ...prev, [campo]: valor }
      if (campo === 'dataInicio' && valor) setCookie(COOKIE_HIST_INI, valor)
      if (campo === 'dataFim' && valor) setCookie(COOKIE_HIST_FIM, valor)
      return next
    })
  }

  const handleAplicarFiltroHistorico = async (e) => {
    e?.preventDefault?.()
    if (filtroHistorico.dataInicio) setCookie(COOKIE_HIST_INI, filtroHistorico.dataInicio)
    if (filtroHistorico.dataFim) setCookie(COOKIE_HIST_FIM, filtroHistorico.dataFim)
    await carregarHistoricoGeral(filtroHistorico)
  }

  const toggleFiltroUsuario = (uid) => {
    setFiltroUsuariosIds((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    )
  }

  const limparFiltroUsuarios = () => setFiltroUsuariosIds([])

  const selecionarTodosFiltroUsuarios = () => {
    setFiltroUsuariosIds(usuariosFiltroLista.map((u) => u.id))
  }

  const handleCreateColumn = async (e) => {
    e.preventDefault()
    if (!formColumn.titulo.trim()) return

    try {
      setSaving(true)
      const nova = await criarColuna({
        empresaId,
        titulo: formColumn.titulo.trim(),
        usuarioId: userId,
        nomeUsuario: userName
      })
      setColunas((prev) => [...prev, nova].sort((a, b) => a.ordem - b.ordem))
      setFormColumn({ titulo: '' })
      setModalColumn(false)
      showMsg('Quadro criado! Ele é privado — compartilhe com outros usuários para eles visualizarem.')
      setSaving(false)
      await openShareModal(nova)
      return
    } catch (err) {
      console.error(err)
      showMsg('Erro ao criar quadro.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleFix = async (coluna) => {
    if (!isPrivilegiado) {
      showMsg('Você precisa de privilégio de administrador/administrativo para fixar quadros.', 'error')
      return
    }
    try {
      const atualizada = await alternarFixacaoColuna({
        coluna,
        usuarioId: userId,
        nomeUsuario: userName
      })
      setColunas((prev) => prev.map((c) => (c.id === atualizada.id ? atualizada : c)))
    } catch (err) {
      console.error(err)
      showMsg('Erro ao alterar fixação do quadro.', 'error')
    }
  }

  const openRenameColumn = (coluna) => {
    if (!podeGerenciarColuna(coluna, userId, isPrivilegiado)) {
      showMsg('Sem permissão para renomear este quadro.', 'error')
      return
    }
    setFormRenameColumn({ titulo: coluna.titulo || '' })
    setModalRenameColumn(coluna)
  }

  const handleRenameColumn = async (e) => {
    e.preventDefault()
    if (!modalRenameColumn || !formRenameColumn.titulo.trim()) return
    try {
      setSaving(true)
      const atualizada = await renomearColuna({
        coluna: modalRenameColumn,
        novoTitulo: formRenameColumn.titulo.trim(),
        usuarioId: userId,
        nomeUsuario: userName
      })
      setColunas((prev) => prev.map((c) => (c.id === atualizada.id ? atualizada : c)))
      setModalRenameColumn(null)
      showMsg('Quadro renomeado!')
    } catch (err) {
      console.error(err)
      showMsg('Erro ao renomear quadro.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleInativarColuna = async (coluna) => {
    if (!podeGerenciarColuna(coluna, userId, isPrivilegiado)) {
      showMsg('Sem permissão para inativar este quadro.', 'error')
      return
    }
    const cardsNaColuna = leads.filter((l) => l.coluna_id === coluna.id).length
    const avisoCards = cardsNaColuna > 0
      ? ` Há ${cardsNaColuna} card(s) neste quadro (eles ficam ocultos junto com o quadro).`
      : ''
    if (!window.confirm(`Inativar o quadro "${coluna.titulo}"?${avisoCards}`)) return
    try {
      setSaving(true)
      await inativarColuna({
        coluna,
        usuarioId: userId,
        nomeUsuario: userName
      })
      setColunas((prev) => prev.filter((c) => c.id !== coluna.id))
      setLeads((prev) => prev.filter((l) => l.coluna_id !== coluna.id))
      showMsg('Quadro inativado.')
    } catch (err) {
      console.error(err)
      showMsg('Erro ao inativar quadro.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const openInactiveColumns = async () => {
    if (!isPrivilegiado) return
    try {
      setSaving(true)
      const lista = await listarColunasInativas(empresaId)
      setColunasInativas(lista)
      setModalInactiveColumns(true)
    } catch (err) {
      console.error(err)
      showMsg('Erro ao listar quadros inativos.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleReativarColuna = async (coluna) => {
    try {
      setSaving(true)
      await reativarColuna({
        coluna,
        usuarioId: userId,
        nomeUsuario: userName
      })
      setColunasInativas((prev) => prev.filter((c) => c.id !== coluna.id))
      showMsg(`Quadro "${coluna.titulo}" reativado.`)
      await carregar()
    } catch (err) {
      console.error(err)
      showMsg('Erro ao reativar quadro.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveContract = async (e) => {
    e.preventDefault()
    if (!modalContract) return
    try {
      setSaving(true)
      const atualizado = await efetivarContrato({
        lead: modalContract,
        razaoSocial: formContract.razaoSocial.trim(),
        documento: formContract.documento.trim(),
        emailFinanceiro: formContract.emailFinanceiro.trim(),
        valorContrato: parseFloat(formContract.valorContrato) || 0,
        usuarioId: userId,
        nomeUsuario: userName
      })
      setLeads((prev) => prev.map((l) => (l.id === atualizado.id ? atualizado : l)))
      setModalContract(null)
      showMsg(`Cadastro do cliente "${atualizado.razao_social}" efetivado!`)
    } catch (err) {
      console.error(err)
      showMsg('Erro ao efetivar cadastro.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const onCardDragStart = (e, leadId) => {
    e.stopPropagation()
    draggedType.current = 'card'
    draggedCardId.current = leadId
    e.currentTarget.classList.add('dragging-card')
    e.dataTransfer.setData('text/plain', leadId)
    e.dataTransfer.effectAllowed = 'move'
  }

  const onCardDragEnd = (e) => {
    e.currentTarget.classList.remove('dragging-card')
    draggedType.current = null
    draggedCardId.current = null
  }

  const onColumnDragStart = (e, coluna) => {
    if (draggedType.current === 'card') return
    if (!isPrivilegiado) {
      e.preventDefault()
      return
    }
    if (coluna.fixada) {
      e.preventDefault()
      showMsg('Este quadro está FIXO e não pode ser movido. Desfixe-o no cadeado.', 'error')
      return
    }
    draggedType.current = 'column'
    draggedColumnId.current = coluna.id
    e.currentTarget.classList.add('dragging-col')
    e.dataTransfer.setData('text/plain', 'column-reorder')
  }

  const onColumnDragEnd = (e) => {
    e.currentTarget.classList.remove('dragging-col')
    document.querySelectorAll('.gestao-leads-column').forEach((col) => {
      col.classList.remove('drag-over-col')
    })
    draggedType.current = null
    draggedColumnId.current = null
  }

  const onColumnDragOver = (e, coluna) => {
    e.preventDefault()
    if (draggedType.current === 'column' && draggedColumnId.current !== coluna.id) {
      e.currentTarget.classList.add('drag-over-col')
    }
  }

  const onColumnDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over-col')
  }

  const onColumnDrop = async (e, targetColuna) => {
    e.preventDefault()
    e.currentTarget.classList.remove('drag-over-col')

    // Reordenar colunas (só privilegiado)
    if (draggedType.current === 'column' && draggedColumnId.current && draggedColumnId.current !== targetColuna.id) {
      if (!isPrivilegiado) return
      const fromId = draggedColumnId.current
      const ordered = [...colunas]
      const fromIdx = ordered.findIndex((c) => c.id === fromId)
      const toIdx = ordered.findIndex((c) => c.id === targetColuna.id)
      if (fromIdx < 0 || toIdx < 0) return
      const [moved] = ordered.splice(fromIdx, 1)
      ordered.splice(toIdx, 0, moved)
      const withOrdem = ordered.map((c, i) => ({ ...c, ordem: i }))
      setColunas(withOrdem)
      try {
        await reordenarColunas({
          empresaId,
          colunaIdsOrdenados: withOrdem.map((c) => c.id),
          usuarioId: userId,
          nomeUsuario: userName
        })
      } catch (err) {
        console.error(err)
        showMsg('Erro ao reordenar quadros.', 'error')
        carregar()
      }
      return
    }

    // Mover card
    if (draggedType.current === 'card' && draggedCardId.current) {
      const lead = leads.find((l) => l.id === draggedCardId.current)
      if (!lead || lead.coluna_id === targetColuna.id) return

      const colunaAnterior = colunasMap[lead.coluna_id]
      const optimistic = { ...lead, coluna_id: targetColuna.id }
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? optimistic : l)))

      try {
        const atualizado = await moverLead({
          lead,
          colunaNovaId: targetColuna.id,
          usuarioId: userId,
          nomeUsuario: userName,
          tituloColunaAnterior: colunaAnterior?.titulo,
          tituloColunaNova: targetColuna.titulo
        })
        setLeads((prev) => prev.map((l) => (l.id === atualizado.id ? atualizado : l)))
      } catch (err) {
        console.error(err)
        showMsg('Erro ao mover lead.', 'error')
        carregar()
      }
    }
  }

  const formatDate = (iso) => {
    if (!iso) return '-'
    try {
      return new Date(iso).toLocaleString('pt-BR')
    } catch {
      return iso
    }
  }

  if (!empresaId) {
    return (
      <div className="gestao-leads-root">
        <div className="gestao-leads-header">
          <h2>Gestão Leads</h2>
          {onBack && (
            <button type="button" className="gestao-leads-btn outline" onClick={onBack}>Voltar</button>
          )}
        </div>
        <div className="gestao-leads-loading">Usuário sem empresa vinculada.</div>
      </div>
    )
  }

  return (
    <div className={`gestao-leads-root${isPrivilegiado ? ' is-admin' : ''}`}>
      <header className="gestao-leads-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {onBack && (
            <button type="button" className="gestao-leads-btn outline" onClick={onBack}>←</button>
          )}
          <h2>Gestão Leads</h2>
        </div>
        <div className="gestao-leads-header-actions">
          {isPrivilegiado && (
            <label className="gestao-leads-toggle-ver-todos" title="Alternar visualização de cards">
              <input
                type="checkbox"
                checked={verTodosCards}
                onChange={(e) => setVerTodosCards(e.target.checked)}
              />
              <span>{verTodosCards ? 'Ver todos os cards' : 'Só meus cards'}</span>
            </label>
          )}
          {isPrivilegiado && verTodosCards && (
            <div className="gestao-leads-user-filter" ref={filtroUsuariosRef}>
              <button
                type="button"
                className="gestao-leads-btn outline"
                onClick={() => setShowFiltroUsuarios((v) => !v)}
                title="Filtrar cards por usuário"
              >
                Usuários{filtroUsuariosIds.length > 0 ? ` (${filtroUsuariosIds.length})` : ''} ▾
              </button>
              {showFiltroUsuarios && (
                <div className="gestao-leads-user-filter-menu">
                  <div className="gestao-leads-user-filter-actions">
                    <button type="button" className="gestao-leads-btn-icon" onClick={selecionarTodosFiltroUsuarios} title="Selecionar todos">
                      Todos
                    </button>
                    <button type="button" className="gestao-leads-btn-icon" onClick={limparFiltroUsuarios} title="Limpar filtro">
                      Limpar
                    </button>
                  </div>
                  <div className="gestao-leads-share-list">
                    {usuariosFiltroLista.map((u) => (
                      <label key={u.id} className="gestao-leads-share-item">
                        <input
                          type="checkbox"
                          checked={filtroUsuariosIds.includes(u.id)}
                          onChange={() => toggleFiltroUsuario(u.id)}
                        />
                        <span>
                          <strong>{u.nome}</strong>
                          {u.email ? <small>{u.email}</small> : null}
                        </span>
                      </label>
                    ))}
                    {usuariosFiltroLista.length === 0 && (
                      <p style={{ color: 'var(--gl-text-sub)', fontSize: '0.85rem', padding: '0.5rem' }}>
                        Nenhum usuário encontrado.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            className="gestao-leads-btn outline"
            onClick={() => setModalRelatorio(true)}
            title="Relatório de desempenho dos especialistas"
          >
            Relatório
          </button>
          {isPrivilegiado && (
            <button
              type="button"
              className="gestao-leads-btn outline"
              onClick={openHistoricoGeral}
              title="Histórico de movimentações dos cards"
            >
              Histórico
            </button>
          )}
          <button type="button" className="gestao-leads-btn" onClick={() => openCreateLead()} disabled={colunas.length === 0}>
            + Novo Lead
          </button>
          <button
            type="button"
            className="gestao-leads-btn secondary"
            onClick={() => { setFormColumn({ titulo: '' }); setModalColumn(true) }}
            title="Criar quadro privado e compartilhar com usuários"
          >
            + Novo Quadro
          </button>
          {isPrivilegiado && (
            <button
              type="button"
              className="gestao-leads-btn outline"
              onClick={openInactiveColumns}
              title="Ver e reativar quadros inativos"
            >
              Quadros inativos
            </button>
          )}
        </div>
      </header>

      {message && (
        <div className={`gestao-leads-msg ${message.type}`}>{message.text}</div>
      )}

      {loading ? (
        <div className="gestao-leads-loading">Carregando Kanban…</div>
      ) : (
        <main className="gestao-leads-board">
          {colunas.map((coluna) => {
            const cards = leadsPorColuna[coluna.id] || []
            return (
              <div
                key={coluna.id}
                className={[
                  'gestao-leads-column',
                  isPrivilegiado && !coluna.fixada ? 'draggable-col' : '',
                  coluna.fixada ? 'is-fixed' : '',
                  coluna.is_contrato_fechado ? 'is-contrato' : ''
                ].filter(Boolean).join(' ')}
                draggable={isPrivilegiado && !coluna.fixada}
                onDragStart={(e) => onColumnDragStart(e, coluna)}
                onDragEnd={onColumnDragEnd}
                onDragOver={(e) => onColumnDragOver(e, coluna)}
                onDragLeave={onColumnDragLeave}
                onDrop={(e) => onColumnDrop(e, coluna)}
              >
                <div className="gestao-leads-column-header">
                  <div className="gestao-leads-column-title">
                    {isPrivilegiado && !coluna.fixada && (
                      <span className="gestao-leads-drag-handle" title="Arrastar quadro para reordenar">⣿</span>
                    )}
                    <h3>{coluna.titulo}</h3>
                  </div>
                  <div className="gestao-leads-header-right">
                    {podeGerenciarColuna(coluna, userId, isPrivilegiado) && (
                      <>
                        <button
                          type="button"
                          className="gestao-leads-btn-icon"
                          title="Renomear quadro"
                          onClick={() => openRenameColumn(coluna)}
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          className="gestao-leads-btn-icon"
                          title="Compartilhar quadro com usuários"
                          onClick={() => openShareModal(coluna)}
                        >
                          👥
                        </button>
                        <button
                          type="button"
                          className="gestao-leads-btn-icon"
                          title="Inativar quadro"
                          onClick={() => handleInativarColuna(coluna)}
                        >
                          ⏸️
                        </button>
                      </>
                    )}
                    {coluna.created_by === userId ? (
                      <span className="gestao-leads-badge-count" title="Você criou / gerencia este quadro" style={{ fontSize: '0.7rem' }}>
                        meu
                      </span>
                    ) : coluna.visivel_empresa ? (
                      <span className="gestao-leads-badge-count" title="Quadro padrão da empresa" style={{ fontSize: '0.7rem' }}>
                        padrão
                      </span>
                    ) : (
                      <span className="gestao-leads-badge-count" title="Compartilhado com você" style={{ fontSize: '0.7rem' }}>
                        compartilhado
                      </span>
                    )}
                    <button
                      type="button"
                      className="gestao-leads-btn-lock"
                      onClick={() => handleToggleFix(coluna)}
                      title={
                        isPrivilegiado
                          ? (coluna.fixada ? 'Clique para desfixar' : 'Clique para fixar')
                          : 'Permissão privilegiada necessária'
                      }
                    >
                      {coluna.fixada ? '🔒' : '🔓'}
                    </button>
                    <span className="gestao-leads-badge-count">{cards.length}</span>
                  </div>
                </div>

                <div className="gestao-leads-cards">
                  {cards.map((lead) => (
                    <div
                      key={lead.id}
                      className="gestao-leads-card"
                      draggable
                      onDragStart={(e) => onCardDragStart(e, lead.id)}
                      onDragEnd={onCardDragEnd}
                      onClick={(e) => {
                        if (!e.target.closest('button')) setModalView(lead)
                      }}
                    >
                      <div className="gestao-leads-card-header">
                        <div className="gestao-leads-card-title">{lead.nome}</div>
                        <div className="gestao-leads-card-actions">
                          {podeGerenciarCompartilhamentoCard(lead, userId, isPrivilegiado) && (
                            <button
                              type="button"
                              className="gestao-leads-btn-icon"
                              title="Compartilhar card"
                              onClick={() => openShareLeadModal(lead)}
                            >
                              👥
                            </button>
                          )}
                          <button
                            type="button"
                            className="gestao-leads-btn-icon"
                            title="Editar"
                            onClick={() => openEditLead(lead)}
                          >
                            ✏️
                          </button>
                          <button
                            type="button"
                            className="gestao-leads-btn-icon"
                            title="Histórico"
                            onClick={() => openHistorico(lead)}
                          >
                            📋
                          </button>
                          <button
                            type="button"
                            className="gestao-leads-btn-icon"
                            title="Inativar"
                            onClick={() => handleInativarLead(lead)}
                          >
                            ⏸️
                          </button>
                        </div>
                      </div>
                      <div className="gestao-leads-card-phone">📞 {lead.telefone}</div>
                      {lead.observacoes ? (
                        <div className="gestao-leads-card-obs">{lead.observacoes}</div>
                      ) : null}
                      {coluna.is_contrato_fechado && !lead.cliente_efetivado && (
                        <button
                          type="button"
                          className="gestao-leads-card-contract-btn"
                          onClick={() => {
                            setFormContract({
                              razaoSocial: lead.nome || '',
                              documento: '',
                              emailFinanceiro: '',
                              valorContrato: ''
                            })
                            setModalContract(lead)
                          }}
                        >
                          📋 Cadastrar Cliente
                        </button>
                      )}
                      {lead.cliente_efetivado && (
                        <div className="gestao-leads-card-efetivado">✓ Cliente efetivado</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </main>
      )}

      {/* Modal criar/editar lead */}
      {modalLead && (
        <div className="gestao-leads-modal-overlay" onClick={() => setModalLead(null)}>
          <div className="gestao-leads-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{modalLead === 'create' ? 'Criar Novo Lead' : 'Editar Lead'}</h2>
            <form onSubmit={handleSaveLead}>
              {modalLead === 'create' && (
                <div className="gestao-leads-form-group">
                  <label htmlFor="gl-coluna">Quadro *</label>
                  <select
                    id="gl-coluna"
                    required
                    value={formLead.colunaId}
                    onChange={(e) => setFormLead((f) => ({ ...f, colunaId: e.target.value }))}
                  >
                    {colunas.map((c) => (
                      <option key={c.id} value={c.id}>{c.titulo}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="gestao-leads-form-group">
                <label htmlFor="gl-nome">Nome do Cliente *</label>
                <input
                  id="gl-nome"
                  required
                  value={formLead.nome}
                  onChange={(e) => setFormLead((f) => ({ ...f, nome: e.target.value }))}
                  placeholder="Ex: Maria Silva"
                />
              </div>
              <div className="gestao-leads-form-group">
                <label htmlFor="gl-tel">Telefone *</label>
                <input
                  id="gl-tel"
                  type="tel"
                  required
                  value={formLead.telefone}
                  onChange={(e) => setFormLead((f) => ({ ...f, telefone: e.target.value }))}
                  placeholder="Ex: (11) 98765-4321"
                />
              </div>
              <div className="gestao-leads-form-group">
                <label htmlFor="gl-obs">Observações</label>
                <textarea
                  id="gl-obs"
                  value={formLead.observacoes}
                  onChange={(e) => setFormLead((f) => ({ ...f, observacoes: e.target.value }))}
                  placeholder="Interessado nos serviços..."
                />
              </div>
              <div className="gestao-leads-modal-actions">
                <button type="button" className="gestao-leads-btn outline" onClick={() => setModalLead(null)}>
                  Cancelar
                </button>
                <button type="submit" className="gestao-leads-btn" disabled={saving}>
                  Salvar Lead
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal visualizar */}
      {modalView && (
        <div className="gestao-leads-modal-overlay" onClick={() => setModalView(null)}>
          <div className="gestao-leads-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Detalhes do Lead</h2>
            <div className="gestao-leads-view-field">
              <div className="gestao-leads-view-label">Nome do Cliente</div>
              <div className="gestao-leads-view-value">{modalView.nome}</div>
            </div>
            <div className="gestao-leads-view-field">
              <div className="gestao-leads-view-label">Telefone</div>
              <div className="gestao-leads-view-value">{modalView.telefone}</div>
            </div>
            <div className="gestao-leads-view-field">
              <div className="gestao-leads-view-label">Status / Quadro</div>
              <div className="gestao-leads-view-value">
                {colunasMap[modalView.coluna_id]?.titulo || '-'}
              </div>
            </div>
            <div className="gestao-leads-view-field">
              <div className="gestao-leads-view-label">Observações</div>
              <div className="gestao-leads-view-value">
                {modalView.observacoes || 'Nenhuma observação cadastrada.'}
              </div>
            </div>
            {modalView.cliente_efetivado && (
              <>
                <div className="gestao-leads-view-field">
                  <div className="gestao-leads-view-label">Razão Social</div>
                  <div className="gestao-leads-view-value">{modalView.razao_social || '-'}</div>
                </div>
                <div className="gestao-leads-view-field">
                  <div className="gestao-leads-view-label">CPF / CNPJ</div>
                  <div className="gestao-leads-view-value">{modalView.documento || '-'}</div>
                </div>
                <div className="gestao-leads-view-field">
                  <div className="gestao-leads-view-label">E-mail Financeiro</div>
                  <div className="gestao-leads-view-value">{modalView.email_financeiro || '-'}</div>
                </div>
                <div className="gestao-leads-view-field">
                  <div className="gestao-leads-view-label">Valor do Contrato</div>
                  <div className="gestao-leads-view-value">
                    {modalView.valor_contrato != null
                      ? Number(modalView.valor_contrato).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                      : '-'}
                  </div>
                </div>
              </>
            )}
            <div className="gestao-leads-modal-actions">
              <button type="button" className="gestao-leads-btn outline" onClick={() => setModalView(null)}>
                Fechar
              </button>
              <button
                type="button"
                className="gestao-leads-btn outline"
                onClick={() => { setModalView(null); openHistorico(modalView) }}
              >
                Histórico
              </button>
              {podeGerenciarCompartilhamentoCard(modalView, userId, isPrivilegiado) && (
                <button
                  type="button"
                  className="gestao-leads-btn outline"
                  onClick={() => { setModalView(null); openShareLeadModal(modalView) }}
                >
                  👥 Compartilhar
                </button>
              )}
              <button
                type="button"
                className="gestao-leads-btn danger"
                onClick={() => handleInativarLead(modalView)}
              >
                Inativar
              </button>
              <button
                type="button"
                className="gestao-leads-btn"
                onClick={() => { setModalView(null); openEditLead(modalView) }}
              >
                ✏️ Editar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal contrato */}
      {modalContract && (
        <div className="gestao-leads-modal-overlay" onClick={() => setModalContract(null)}>
          <div className="gestao-leads-modal" onClick={(e) => e.stopPropagation()}>
            <h2>📋 Cadastro Completo do Cliente</h2>
            <p style={{ fontSize: '0.85rem', color: 'var(--gl-text-sub)', marginBottom: '1rem' }}>
              Contrato fechado! Preencha as informações cadastrais para efetivar o cliente.
            </p>
            <form onSubmit={handleSaveContract}>
              <div className="gestao-leads-form-group">
                <label>Razão Social / Nome Completo *</label>
                <input
                  required
                  value={formContract.razaoSocial}
                  onChange={(e) => setFormContract((f) => ({ ...f, razaoSocial: e.target.value }))}
                />
              </div>
              <div className="gestao-leads-form-group">
                <label>CPF / CNPJ *</label>
                <input
                  required
                  value={formContract.documento}
                  onChange={(e) => setFormContract((f) => ({ ...f, documento: e.target.value }))}
                  placeholder="00.000.000/0000-00"
                />
              </div>
              <div className="gestao-leads-form-group">
                <label>E-mail Financeiro *</label>
                <input
                  type="email"
                  required
                  value={formContract.emailFinanceiro}
                  onChange={(e) => setFormContract((f) => ({ ...f, emailFinanceiro: e.target.value }))}
                />
              </div>
              <div className="gestao-leads-form-group">
                <label>Valor do Contrato (R$) *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formContract.valorContrato}
                  onChange={(e) => setFormContract((f) => ({ ...f, valorContrato: e.target.value }))}
                />
              </div>
              <div className="gestao-leads-modal-actions">
                <button type="button" className="gestao-leads-btn outline" onClick={() => setModalContract(null)}>
                  Cancelar
                </button>
                <button type="submit" className="gestao-leads-btn secondary" disabled={saving}>
                  Efetivar Cadastro
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal nova coluna */}
      {modalColumn && (
        <div className="gestao-leads-modal-overlay" onClick={() => setModalColumn(false)}>
          <div className="gestao-leads-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Novo Quadro (Coluna)</h2>
            <p style={{ fontSize: '0.85rem', color: 'var(--gl-text-sub)', marginBottom: '1rem' }}>
              O quadro nasce privado. Depois da criação, escolha com quem compartilhar para eles verem e criarem leads.
            </p>
            <form onSubmit={handleCreateColumn}>
              <div className="gestao-leads-form-group">
                <label>Nome do Quadro *</label>
                <input
                  required
                  value={formColumn.titulo}
                  onChange={(e) => setFormColumn({ titulo: e.target.value })}
                  placeholder="Ex: Mascote"
                />
              </div>
              <div className="gestao-leads-modal-actions">
                <button type="button" className="gestao-leads-btn outline" onClick={() => setModalColumn(false)}>
                  Cancelar
                </button>
                <button type="submit" className="gestao-leads-btn secondary" disabled={saving}>
                  Criar e Compartilhar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal renomear quadro */}
      {modalRenameColumn && (
        <div className="gestao-leads-modal-overlay" onClick={() => setModalRenameColumn(null)}>
          <div className="gestao-leads-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Renomear Quadro</h2>
            <form onSubmit={handleRenameColumn}>
              <div className="gestao-leads-form-group">
                <label>Novo nome *</label>
                <input
                  required
                  value={formRenameColumn.titulo}
                  onChange={(e) => setFormRenameColumn({ titulo: e.target.value })}
                  placeholder="Nome do quadro"
                  autoFocus
                />
              </div>
              <div className="gestao-leads-modal-actions">
                <button type="button" className="gestao-leads-btn outline" onClick={() => setModalRenameColumn(null)}>
                  Cancelar
                </button>
                <button type="submit" className="gestao-leads-btn" disabled={saving}>
                  Salvar nome
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal quadros inativos */}
      {modalInactiveColumns && (
        <div className="gestao-leads-modal-overlay" onClick={() => setModalInactiveColumns(false)}>
          <div className="gestao-leads-modal wide" onClick={(e) => e.stopPropagation()}>
            <h2>Quadros inativos</h2>
            <p style={{ fontSize: '0.85rem', color: 'var(--gl-text-sub)', marginBottom: '1rem' }}>
              Inclui quadros padrão e personalizados. Reative para voltarem ao Kanban.
            </p>
            {colunasInativas.length === 0 ? (
              <p style={{ color: 'var(--gl-text-sub)' }}>Nenhum quadro inativo.</p>
            ) : (
              <ul className="gestao-leads-historico-list">
                {colunasInativas.map((col) => (
                  <li key={col.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
                    <span>
                      <strong>{col.titulo}</strong>
                      {col.visivel_empresa ? (
                        <span className="gestao-leads-historico-meta">Quadro padrão</span>
                      ) : null}
                    </span>
                    <button
                      type="button"
                      className="gestao-leads-btn secondary"
                      disabled={saving}
                      onClick={() => handleReativarColuna(col)}
                    >
                      Reativar
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="gestao-leads-modal-actions">
              <button type="button" className="gestao-leads-btn outline" onClick={() => setModalInactiveColumns(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      <GestaoLeadsRelatorio
        show={modalRelatorio}
        onClose={() => setModalRelatorio(false)}
        empresaId={empresaId}
        isPrivilegiado={isPrivilegiado}
        userId={userId}
        usuarios={usuariosFiltroLista}
        filtroUsuariosIds={filtroUsuariosIds}
      />

      {/* Modal histórico geral de movimentações (admin/administrativo) */}
      {modalHistoricoGeral && (
        <div className="gestao-leads-modal-overlay" onClick={() => setModalHistoricoGeral(false)}>
          <div className="gestao-leads-modal xwide" onClick={(e) => e.stopPropagation()}>
            <h2>Histórico de movimentações</h2>
            <p style={{ fontSize: '0.85rem', color: 'var(--gl-text-sub)', marginBottom: '1rem' }}>
              Movimentações dos cards/leads por usuário. As datas ficam salvas nos cookies.
            </p>
            <form className="gestao-leads-hist-filtros" onSubmit={handleAplicarFiltroHistorico}>
              <div className="gestao-leads-form-group">
                <label>Data início</label>
                <input
                  type="date"
                  required
                  value={filtroHistorico.dataInicio}
                  onChange={(e) => handleChangeFiltroHistorico('dataInicio', e.target.value)}
                />
              </div>
              <div className="gestao-leads-form-group">
                <label>Data fim</label>
                <input
                  type="date"
                  required
                  value={filtroHistorico.dataFim}
                  onChange={(e) => handleChangeFiltroHistorico('dataFim', e.target.value)}
                />
              </div>
              <div className="gestao-leads-form-group">
                <label>Usuário</label>
                <select
                  value={filtroHistorico.usuarioId}
                  onChange={(e) => handleChangeFiltroHistorico('usuarioId', e.target.value)}
                >
                  <option value="">Todos os usuários</option>
                  {usuariosFiltroLista.map((u) => (
                    <option key={u.id} value={u.id}>{u.nome}</option>
                  ))}
                </select>
              </div>
              <div className="gestao-leads-form-group gestao-leads-hist-filtros-btn">
                <label>&nbsp;</label>
                <button type="submit" className="gestao-leads-btn" disabled={historicoGeralLoading}>
                  Filtrar
                </button>
              </div>
            </form>

            {historicoGeralLoading ? (
              <p style={{ color: 'var(--gl-text-sub)' }}>Carregando…</p>
            ) : historicoGeralItems.length === 0 ? (
              <p style={{ color: 'var(--gl-text-sub)' }}>Nenhuma movimentação no período.</p>
            ) : (
              <div className="gestao-leads-hist-table-wrap">
                <table className="gestao-leads-hist-table">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Usuário</th>
                      <th>Lead</th>
                      <th>Ação</th>
                      <th>Detalhe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historicoGeralItems.map((h) => (
                      <tr key={h.id}>
                        <td>{formatDate(h.created_at)}</td>
                        <td>{h.nome_usuario || '—'}</td>
                        <td>
                          {h.lead?.nome || '—'}
                          {h.lead?.telefone ? (
                            <small className="gestao-leads-historico-meta">{h.lead.telefone}</small>
                          ) : null}
                        </td>
                        <td><code>{h.tipo_acao}</code></td>
                        <td>{h.observacoes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="gestao-leads-modal-actions">
              <button type="button" className="gestao-leads-btn outline" onClick={() => setModalHistoricoGeral(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal compartilhar quadro */}
      {modalShare && (
        <div className="gestao-leads-modal-overlay" onClick={() => setModalShare(null)}>
          <div className="gestao-leads-modal wide" onClick={(e) => e.stopPropagation()}>
            <h2>Compartilhar — {modalShare.titulo}</h2>
            <p style={{ fontSize: '0.85rem', color: 'var(--gl-text-sub)', marginBottom: '1rem' }}>
              Selecione os usuários que poderão visualizar este quadro e criar leads nele.
              {modalShare.visivel_empresa
                ? ' Enquanto houver usuários selecionados, o acesso fica restrito a eles (além do criador e privilegiados). Sem seleção, o quadro padrão volta a ficar aberto para a empresa.'
                : ''}
            </p>
            <form onSubmit={handleSaveShare}>
              <div className="gestao-leads-form-group">
                <label>Buscar usuário</label>
                <input
                  type="search"
                  value={shareSearch}
                  onChange={(e) => setShareSearch(e.target.value)}
                  placeholder="Nome ou e-mail"
                />
              </div>
              <div className="gestao-leads-share-list">
                {usuariosEmpresa
                  .filter((u) => {
                    const q = shareSearch.trim().toLowerCase()
                    if (!q) return true
                    return (
                      u.nome?.toLowerCase().includes(q) ||
                      u.email?.toLowerCase().includes(q)
                    )
                  })
                  .map((u) => (
                    <label key={u.id} className="gestao-leads-share-item">
                      <input
                        type="checkbox"
                        checked={shareSelectedIds.includes(u.id)}
                        onChange={() => toggleShareUser(u.id)}
                      />
                      <span>
                        <strong>{u.nome}</strong>
                        {u.email ? <small>{u.email}</small> : null}
                      </span>
                    </label>
                  ))}
                {usuariosEmpresa.length === 0 && (
                  <p style={{ color: 'var(--gl-text-sub)', fontSize: '0.9rem' }}>
                    Nenhum outro usuário ativo encontrado nesta empresa.
                  </p>
                )}
              </div>
              <div className="gestao-leads-modal-actions">
                <button type="button" className="gestao-leads-btn outline" onClick={() => setModalShare(null)}>
                  Agora não
                </button>
                <button type="submit" className="gestao-leads-btn" disabled={saving}>
                  Salvar compartilhamento ({shareSelectedIds.length})
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal compartilhar card/lead */}
      {modalShareLead && (
        <div className="gestao-leads-modal-overlay" onClick={() => setModalShareLead(null)}>
          <div className="gestao-leads-modal wide" onClick={(e) => e.stopPropagation()}>
            <h2>Compartilhar card — {modalShareLead.nome}</h2>
            <p style={{ fontSize: '0.85rem', color: 'var(--gl-text-sub)', marginBottom: '1rem' }}>
              Por padrão só o dono vê este card. Selecione quem mais poderá visualizá-lo.
            </p>
            <form onSubmit={handleSaveShareLead}>
              <div className="gestao-leads-form-group">
                <label>Buscar usuário</label>
                <input
                  type="search"
                  value={shareSearch}
                  onChange={(e) => setShareSearch(e.target.value)}
                  placeholder="Nome ou e-mail"
                />
              </div>
              <div className="gestao-leads-share-list">
                {usuariosEmpresa
                  .filter((u) => {
                    const q = shareSearch.trim().toLowerCase()
                    if (!q) return true
                    return (
                      u.nome?.toLowerCase().includes(q) ||
                      u.email?.toLowerCase().includes(q)
                    )
                  })
                  .map((u) => (
                    <label key={u.id} className="gestao-leads-share-item">
                      <input
                        type="checkbox"
                        checked={shareSelectedIds.includes(u.id)}
                        onChange={() => toggleShareUser(u.id)}
                      />
                      <span>
                        <strong>{u.nome}</strong>
                        {u.email ? <small>{u.email}</small> : null}
                      </span>
                    </label>
                  ))}
                {usuariosEmpresa.length === 0 && (
                  <p style={{ color: 'var(--gl-text-sub)', fontSize: '0.9rem' }}>
                    Nenhum outro usuário ativo encontrado nesta empresa.
                  </p>
                )}
              </div>
              <div className="gestao-leads-modal-actions">
                <button type="button" className="gestao-leads-btn outline" onClick={() => setModalShareLead(null)}>
                  Cancelar
                </button>
                <button type="submit" className="gestao-leads-btn" disabled={saving}>
                  Salvar ({shareSelectedIds.length})
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal histórico */}
      {modalHistorico && (
        <div className="gestao-leads-modal-overlay" onClick={() => setModalHistorico(null)}>
          <div className="gestao-leads-modal wide" onClick={(e) => e.stopPropagation()}>
            <h2>Histórico — {modalHistorico.nome}</h2>
            {historicoItems.length === 0 ? (
              <p style={{ color: 'var(--gl-text-sub)' }}>Nenhuma movimentação registrada.</p>
            ) : (
              <ul className="gestao-leads-historico-list">
                {historicoItems.map((h) => (
                  <li key={h.id}>
                    <strong>{h.tipo_acao}</strong>
                    {h.observacoes ? ` — ${h.observacoes}` : ''}
                    <div className="gestao-leads-historico-meta">
                      {h.nome_usuario || 'Sistema'} · {formatDate(h.created_at)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="gestao-leads-modal-actions">
              <button type="button" className="gestao-leads-btn outline" onClick={() => setModalHistorico(null)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GestaoLeads
